<template>
  <div class="row g-2 mb-4">
    <div class="col-12 d-flex align-items-center mb-1">
      <h2 class="h6 small fw-bold text-uppercase text-primary mb-0">
        {{ isDutch ? 'Beschikbare reward tickets' : 'Available Reward tickets (seats)' }}
      </h2>
      <span class="badge bg-primary-darken border rounded-pill ms-2">Beta</span>
    </div>
    <div class="col-12">
      <div class="card no-hover py-2 bg-blurry">
        <div class="table-responsive-md">
          <table class="table table-hover align-middle position-relative mb-0" itemscope itemtype="https://schema.org/Table">
            <thead>
            <tr>
              <th scope="col" class="small text-uppercase opacity-75 fw-normal pt-0">{{ isDutch ? "Datum" : "Date" }}</th>
              <th class="small text-uppercase opacity-75 fw-normal pt-0">
                <span class="d-md-none">Eco.</span>
                <span class="d-none d-md-block">Economy</span>
              </th>
              <th class="small text-uppercase opacity-75 fw-normal pt-0">
                <span class="d-md-none">Prem.</span>
                <span class="d-none d-md-block">Premium</span>
              </th>
              <th class="small text-uppercase opacity-75 fw-normal pt-0">
                <span class="d-md-none">Biz.</span>
                <span class="d-none d-md-block">Business</span>
              </th>
              <th class="small text-uppercase opacity-75 fw-normal pt-0"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="ticket in tickets.slice(0, limitTickets)"
                @click="openLink(generateURL(moment(ticket.travel_date, 'YYYY-MM-DD').format('YYYYMMDD')))"
                :class="{'today' : generateReadableDate(ticket.travel_date) === ( isDutch ? 'Vandaag' : 'Today'), 'weekend' : isWeekend(ticket.travel_date)}">
              <th>{{ generateReadableDate(ticket.travel_date) }}</th>
              <th>
                <p :class="promoReward && promoReward.miles === ticket.availability.economy?.miles ? 'bg-primary-darken' : 'bg-blurry'" class="badge rounded-pill border mb-0">
                  <span>{{ ticket.availability.economy ? Intl.NumberFormat("nl").format(ticket.availability.economy.miles) : '×' }}</span>
                  <span v-if="ticket.availability.economy && ticket.availability.economy.miles > getLowestPrice('economy')" class="ms-1 text-danger">↑</span>
                  <span v-else-if="ticket.availability.economy && ticket.availability.economy.miles < getLowestPrice('economy')" class="ms-1 text-success">↓</span>
                </p>
              </th>
              <th>
                <p :class="promoReward && promoReward.miles === ticket.availability.premium?.miles ? 'bg-primary-darken' : 'bg-blurry'" class="badge rounded-pill border mb-0">
                  <span>{{ ticket.availability.premium ? Intl.NumberFormat("nl").format(ticket.availability.premium.miles) : '×' }}</span>
                  <span v-if="ticket.availability.premium && ticket.availability.premium.miles > getLowestPrice('premium')" class="ms-1 text-danger">↑</span>
                  <span v-else-if="ticket.availability.premium && ticket.availability.premium.miles < getLowestPrice('premium')" class="ms-1 text-success">↓</span>
                </p>
              </th>
              <th>
                <p :class="promoReward && promoReward.miles === ticket.availability.business?.miles ? 'bg-primary-darken' : 'bg-blurry'" class="badge rounded-pill border mb-0">
                  <span>{{ ticket.availability.business ? Intl.NumberFormat("nl").format(ticket.availability.business.miles) : '×' }}</span>
                  <span v-if="ticket.availability.business && ticket.availability.business.miles > getLowestPrice('business')" class="ms-1 text-danger">↑</span>
                  <span v-else-if="ticket.availability.business && ticket.availability.business.miles < getLowestPrice('business')" class="ms-1 text-success">↓</span>
                </p>
              </th>
              <th class="text-end">
                →
              </th>
            </tr>
            </tbody>
            <tfoot class="blur" v-if="!authStore.user && tickets.length > limitTickets">
            <tr v-for="n in 4">
              <td>-</td>
              <td>
                <p class="badge rounded-pill bg-blurry border mb-0">
                  13.37
                </p>
              </td>
              <td>
                <p class="badge rounded-pill bg-blurry border mb-0">
                  NICE
                </p>
              </td>
              <td>
                <p class="badge rounded-pill bg-blurry border mb-0">
                  TRY
                </p>
              </td>
              <td class="text-end">
                →
              </td>
            </tr>
            </tfoot>
          </table>
          <div v-if="!authStore.user" class="table-disclaimer">
            <div class="bg-transparent text-center">
              <NuxtImg
                  class="mb-2"
                  alt="Pilot Emoji"
                  width="30"
                  height="30"
                  loading="lazy"
                  src="img/pilot-emoji.png"
              />
              <p v-if="isDutch" class="h3 text-primary">Meer zien? Meld je aan!</p>
              <p v-else class="h3 text-primary">Get Full Access</p>

              <p v-if="isDutch" class="mb-0">Voor volledige toegang, maak een <b>gratis account</b> of <b>log in</b>.</p>
              <p v-else class="mb-0">For full access, create a <b>free account</b> or <b>log in</b>.</p>

              <p v-if="isDutch" class="mb-0">Klik op <b>Account</b> in het menu om in te loggen.</p>
              <p v-else class="mb-0">Tap <b>Account</b> in the menu to get started.</p>
            </div>
          </div>
        </div>
        <div class="d-sm-flex px-3 align-items-center mt-2">
          <p class="small mb-0 me-2" v-if="promoReward"><span class="text-primary">●</span> {{ isDutch ? 'Promo tarief' : 'Promo fare' }}</p>
          <p class="small mb-0 me-2"><span class="text-danger">↑</span> {{ isDutch ? 'Boven laagste tarief' : 'Above standard fare' }}</p>
          <p class="small mb-0 me-2"><span class="text-success">↓</span> {{ isDutch ? 'Onder laagste tarief' : 'Under standard fare' }}</p>
          <p class="small mb-0 me-2"><span class="text-white">×</span> {{ isDutch ? 'Niet beschikbaar' : 'Not available' }}</p>
          <p class="small mb-0"><span class="text-purple">●</span> Weekend</p>
        </div>
        <div class="d-flex px-3 align-items-center">
          <p v-if="isDutch" class="mb-0 mt-1 small opacity-75">
            <span class="fw-bold">Beta:</span> getoonde data is niet real-time en kan verouderd zijn.
          </p>
          <p v-else class="mb-0 mt-1 small opacity-75">
            <span  class="fw-bold">Beta:</span> displayed data is not real-time and may be outdated.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import moment from "moment";
import {useAuthStore} from "~/store/auth.js";

interface Ticket {
  availability: {
    economy?: { miles: number },
    premium?: { miles: number },
    business?: { miles: number }
  },
  travel_date: string
}

interface Props {
  tickets: Ticket[],
  promoReward?: any,
  airports: string[]
}

type CabinClass = 'economy' | 'premium' | 'business';

const authStore = useAuthStore();
const isDutch = inject('isDutch');

const props = defineProps<Props>();

const limitTickets = computed(() => {
  return authStore.user ? props.tickets.length : 7;
});

const getLowestPrice = (cabinClass: CabinClass) => {
  let lowestPrice = Infinity;

  for (const ticket of props.tickets) {
    const cabin = ticket.availability[cabinClass];
    if (cabin && cabin.miles && cabin.miles >= 1 && cabin.miles < lowestPrice) {
      lowestPrice = cabin.miles;
    }
  }

  return lowestPrice === Infinity ? null : lowestPrice;
};

const generateReadableDate = computed(() => {
  return (date: moment.MomentInput) => {
    if(moment(date).isSame(moment(), 'day')) {
      return isDutch ? 'Vandaag' : 'Today'
    } else if(moment(date).isSame(moment().add(1, 'days'), 'day')) {
      return isDutch ? 'Morgen' : 'Tomorrow'
    }
    return moment(date, "YYYY-MM-DD").format('D MMM')
  }
});

const isWeekend = computed(() => {
  return (date: moment.MomentInput) => {
    const day = moment(date).day();
    return day === 0 || day === 6;
  }
});

const generateURL = (date: string) => {
  return 'https://prf.hn/click/camref:1100laQj/creativeref:1100l65362/ar:rewards.flights/pubref:'+ props.airports[1] +'/destination:https://www.klm.nl/search/offers?pax=1:0:0:0:0:0:0:0&activeConnection=0&cabinClass=ECONOMY&connections='+ props.airports[0] +':A:'+ date +'>'+ props.airports[1] +':A&bookingFlow=REWARD'
};

function openLink(url: string | URL | undefined) {
  setTimeout(() => {
    window.open(url, '_blank');
  })
}
</script>